/**
 * Subscription Manager Service
 * 
 * This service manages the integration between the RevenueCat IAP service
 * and Firebase subscription status updates. It listens for purchase
 * updates and automatically syncs the subscription status.
 * 
 * Updated to use the new unified IAP service with RevenueCat.
 */

import { store } from '../../store';
import { setEntitlements } from '../../store/slices/premiumSlice';
import { initializeIAP, getSubscriptionStatus, syncSubscriptionStatus } from '../iap/unified-iap.service';
import { REVENUECAT_CONFIG } from '../iap/config';
import { PremiumFeature } from '../../types/premium';
import logger from '../../utils/logger';

/**
 * Initialize the subscription manager and set up purchase listeners
 * This should be called after user authentication
 */
export const initializeSubscriptionManager = async (userId: string): Promise<void> => {
  try {
    // Skip if IAP is disabled
    if (!REVENUECAT_CONFIG.featureFlags.enablePhoenixIAP) {
      logger.info('Subscription Manager: IAP disabled via feature flag');
      return;
    }

    // Initialize the unified IAP service
    await initializeIAP(userId);
    
    logger.info('Subscription Manager: Initialized with RevenueCat');

    // Do initial subscription check
    await checkAndUpdateSubscriptionStatus();
    
    // Note: RevenueCat handles its own listeners internally
    // The unified IAP service will call syncSubscriptionStatus after purchases
  } catch (error) {
    logger.error('Failed to initialize subscription manager:', error);
  }
};

/**
 * Check current entitlements and update subscription status in Firebase
 */
export const checkAndUpdateSubscriptionStatus = async (): Promise<void> => {
  try {
    // Use the unified service to sync subscription status
    await syncSubscriptionStatus();
    
    // Get current subscription status
    const status = await getSubscriptionStatus();
    
    // Update premium slice with entitlements
    const features: PremiumFeature[] = [];
    if (status.isPeakSubscriber) {
      // Peak includes all features
      features.push(
        PremiumFeature.INJURY_TRACKING,
        PremiumFeature.PHOTO_GALLERY,
        PremiumFeature.CUSTOM_METRICS,
        PremiumFeature.AD_FREE,
        PremiumFeature.GOALS,
        PremiumFeature.WORKOUT_ROUTINES,
        PremiumFeature.INSIGHTS,
        PremiumFeature.ROUND_TRACKER,
        PremiumFeature.MULTIPLE_MARTIAL_ARTS,
        PremiumFeature.LEADERBOARDS
      );
    } else if (status.isAdFree) {
      features.push(PremiumFeature.AD_FREE);
    }
    
    store.dispatch(setEntitlements({
      isPeakSubscriber: status.isPeakSubscriber,
      isAdFree: status.isAdFree,
      features
    }));
    
    logger.info('Subscription status check completed');
  } catch (error) {
    logger.error('Failed to check and update subscription status:', error);
  }
};

/**
 * Clean up subscription manager (call on logout)
 */
export const cleanupSubscriptionManager = async (): Promise<void> => {
  try {
    // Log out of RevenueCat
    const { unifiedIAPService } = await import('../iap/unified-iap.service');
    await unifiedIAPService.logOut();
    logger.info('Subscription Manager: Cleaned up');
  } catch (error) {
    logger.error('Failed to cleanup subscription manager:', error);
  }
};

/**
 * Manually trigger a subscription status check
 * Useful for debugging or forcing a sync
 */
export const forceSubscriptionSync = async (): Promise<void> => {
  logger.info('Forcing subscription status sync...');
  await checkAndUpdateSubscriptionStatus();
};

// Export for debugging in console
if (typeof window !== 'undefined') {
  (window as any).subscriptionManager = {
    initialize: initializeSubscriptionManager,
    checkStatus: checkAndUpdateSubscriptionStatus,
    forceSync: forceSubscriptionSync,
    cleanup: cleanupSubscriptionManager
  };
}